import { useRef, useState } from "react";
import { FileSpreadsheet, Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi, errMsg } from "@/lib/api";

const ResultDialog = ({ res, onClose }) => (
  <Dialog open={!!res} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="excel-import-result">
      <DialogHeader><DialogTitle>Importazione completata</DialogTitle></DialogHeader>
      {res && (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Aggiunti", res.added, "excel-added"], ["Aggiornati", res.updated, "excel-updated"], ["Pacchetti salvati", res.kits, "excel-kits"]].map(([l, v, id]) => (
              <div key={l} className="bg-slate-800/60 rounded p-3"><div className="font-mono text-2xl text-amber-400" data-testid={id}>{v}</div><div className="micro-label mt-1">{l}</div></div>
            ))}
          </div>
          {res.new_sections.length > 0 && (
            <p className="text-amber-300">Sezioni nuove non presenti in Impostazioni: {res.new_sections.join(", ")}. Aggiungile in Impostazioni per mostrarle nell'ordine giusto.</p>
          )}
          {res.error_count > 0 && (
            <div>
              <p className="text-red-300 font-medium">{res.error_count} righe non importate:</p>
              <ul className="mt-1 max-h-48 overflow-y-auto text-xs text-slate-300 space-y-1" data-testid="excel-errors">
                {res.errors.map((e) => <li key={e}>• {e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <DialogFooter><Button onClick={onClose} data-testid="excel-result-close">Chiudi</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ExcelTools = ({ onImported }) => {
  const api = useApi();
  const input = useRef(null);
  const [busy, setBusy] = useState(null);
  const [res, setRes] = useState(null);

  const download = async () => {
    setBusy("down");
    try {
      const r = await api.get("/catalog-excel", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "Listino_articoli_kit.xlsx";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (e) { toast.error(errMsg(e, "Download non riuscito")); } finally { setBusy(null); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("up");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/catalog-excel", fd);
      setRes(r.data);
      onImported();
    } catch (e) { toast.error(errMsg(e, "Importazione non riuscita")); } finally {
      setBusy(null);
      input.current.value = "";
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 bg-slate-900 border border-slate-800 rounded-lg p-3" data-testid="excel-tools">
      <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
      <span className="text-sm text-slate-300 mr-auto">Gestisci articoli e kit con Excel: scarica il file, modificalo e ricaricalo.</span>
      <Button variant="secondary" onClick={download} disabled={!!busy} data-testid="excel-download-btn">
        {busy === "down" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Scarica Excel
      </Button>
      <Button onClick={() => input.current.click()} disabled={!!busy} data-testid="excel-upload-btn">
        {busy === "up" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Carica Excel
      </Button>
      <input ref={input} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="excel-file-input" />
      <ResultDialog res={res} onClose={() => setRes(null)} />
    </div>
  );
};
